/**
 * A deterministic, populated view for looking at the board and the mat without
 * a server: open the client with `?demo` (optionally `?demo=<seed>&players=3`).
 *
 * It runs the real engine, so what you see is a state the game can actually
 * reach — no hand-built fixtures that drift away from the rules.
 */
import {
  newGame,
  randomPlayout,
  viewFor,
  type GameEvent,
  type PlayerCount,
  type PlayerId,
  type View,
} from '@trajan/engine';

export interface Demo {
  view: View;
  /** Everything the playout emitted, in order — the game log as a room would hold it. */
  events: GameEvent[];
  /** The seat the view belongs to, and whose mat is shown. */
  seat: PlayerId;
  /** Which pane to render on its own; all of them by default. */
  only: 'board' | 'mat' | 'panel' | 'log' | null;
  /** A tray whose forecast path is held open, so a still picture shows it. */
  hover: number | null;
  /** Makes the mat's trays clickable and focusable, as they are on your turn. */
  active: boolean;
  /** Opens placement on a tray, so a still picture shows the arranging step. */
  place: number | null;
  /**
   * Milliseconds between revealed log entries. The log arrives a command at a
   * time in a real game, and how it behaves while it grows — following the
   * newest line, but not while you are reading further up — can only be seen
   * if it grows, so the preview can make it grow.
   */
  tick: number | null;
}

export function demoView(
  seed = 'demo',
  playerCount: PlayerCount = 3,
  commands = 120,
  seat: PlayerId = 0,
): { view: View; events: GameEvent[] } {
  const start = newGame({ seed, playerCount });
  const { state, events } = randomPlayout(start, `${seed}-bot`, commands);
  return { view: viewFor(state, seat), events };
}

/** `?demo` / `?demo=seed&players=4&commands=200&seat=3&only=mat&hover=3&place=2&active=1&tick=300`, or null when not in demo mode. */
export function demoFromLocation(search: string): Demo | null {
  const params = new URLSearchParams(search);
  if (!params.has('demo')) return null;
  const seed = params.get('demo') || 'demo';
  const players = Number(params.get('players') ?? 3);
  const playerCount = (
    players === 2 || players === 3 || players === 4 ? players : 3
  ) as PlayerCount;
  const commands = Number(params.get('commands') ?? 120);
  const only = params.get('only');
  // Number(null) is 0, so an absent `hover` would silently open tray 0.
  const hover = params.has('hover') ? Number(params.get('hover')) : NaN;
  const place = params.has('place') ? Number(params.get('place')) : NaN;
  const tick = params.has('tick') ? Number(params.get('tick')) : NaN;
  const asked = Number(params.get('seat') ?? 0);
  const seat = (
    Number.isInteger(asked) && asked >= 0 && asked < playerCount ? asked : 0
  ) as PlayerId;
  const played = demoView(seed, playerCount, Number.isFinite(commands) ? commands : 120, seat);
  return {
    view: played.view,
    events: played.events,
    seat,
    only: only === 'board' || only === 'mat' || only === 'panel' || only === 'log' ? only : null,
    active: params.get('active') === '1',
    place: Number.isInteger(place) && place >= 0 && place < 6 ? place : null,
    hover: Number.isInteger(hover) && hover >= 0 && hover < 6 ? hover : null,
    tick: Number.isFinite(tick) && tick > 0 ? tick : null,
  };
}
